import * as tus from "tus-js-client";

const movieVideoUploadPath = "/movies/upload-movie-video";
const trailerVideoUploadPath = "/movies/trailer-video-upload";
const MAX_VIDEO_UPLOAD_BYTES = 8388608000;
const VIDEO_UPLOAD_CHUNK_SIZE = 50 * 1024 * 1024;
const UPLOAD_DURATION_BUFFER_SECONDS = 15 * 60;
const MOVIE_UPLOAD_MIN_DURATION_SECONDS = 4 * 60 * 60;
const TRAILER_UPLOAD_MIN_DURATION_SECONDS = 60 * 60;

const getApiBaseUrl = () =>
  (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

const getUploadEndpoint = (uploadPath) => `${getApiBaseUrl()}${uploadPath}`;

const isBackendUploadRequest = (url, uploadPath) => {
  const apiBaseUrl = getApiBaseUrl();
  if (apiBaseUrl) return url.startsWith(apiBaseUrl);

  return url.startsWith(uploadPath);
};

const extractCloudflareVideoId = (value) => {
  if (!value) return "";

  try {
    const url = new URL(value);
    const parts = url.pathname.split("/").filter(Boolean);
    return parts[parts.length - 1] || "";
  } catch {
    const parts = String(value).split("?")[0].split("/").filter(Boolean);
    return parts[parts.length - 1] || "";
  }
};

const getMaxDurationSeconds = (durationMinutes, minimumDurationSeconds) => {
  const duration = Number(durationMinutes);
  if (!Number.isFinite(duration) || duration <= 0) return minimumDurationSeconds;

  return Math.max(
    Math.ceil(duration * 60) + UPLOAD_DURATION_BUFFER_SECONDS,
    minimumDurationSeconds,
  );
};

function uploadVideoToCloudflare({
  durationMinutes,
  file,
  minimumDurationSeconds,
  onProgress,
  playbackBaseUrl,
  uploadPath,
}) {
  if (!file) {
    return Promise.reject(new Error("Please select video"));
  }

  if (file.size > MAX_VIDEO_UPLOAD_BYTES) {
    return Promise.reject(
      new Error("The video file size should not be more than 8 GB"),
    );
  }

  return new Promise((resolve, reject) => {
    let streamMediaId = "";
    const adminToken = localStorage.getItem("token") || "";

    const upload = new tus.Upload(file, {
      chunkSize: VIDEO_UPLOAD_CHUNK_SIZE,
      endpoint: getUploadEndpoint(uploadPath),
      metadata: {
        filename: file.name,
        filetype: file.type || "video/mp4",
        maxDurationSeconds: String(
          getMaxDurationSeconds(durationMinutes, minimumDurationSeconds),
        ),
        name: file.name,
      },
      onAfterResponse: (request, response) => {
        const mediaId = response.getHeader("stream-media-id");
        if (mediaId) streamMediaId = mediaId;
      },
      onBeforeRequest: (request) => {
        if (isBackendUploadRequest(request.getURL(), uploadPath) && adminToken) {
          request.setHeader("x-auth-token", adminToken);
        }
      },
      onError: reject,
      onProgress: (bytesUploaded, bytesTotal) => {
        if (!bytesTotal) return;
        onProgress?.(((bytesUploaded * 100) / bytesTotal).toFixed());
      },
      onSuccess: () => {
        const videoId = streamMediaId || extractCloudflareVideoId(upload.url);

        if (!videoId) {
          reject(new Error("Cloudflare did not return a video ID."));
          return;
        }

        resolve({
          playbackUrl: `${playbackBaseUrl}/${videoId}`,
          videoId,
        });
      },
      retryDelays: [0, 3000, 5000, 10000, 20000],
    });

    upload.start();
  });
}

export function uploadMovieVideoToCloudflare(options) {
  return uploadVideoToCloudflare({
    ...options,
    minimumDurationSeconds: MOVIE_UPLOAD_MIN_DURATION_SECONDS,
    playbackBaseUrl: "https://watch.videodelivery.net",
    uploadPath: movieVideoUploadPath,
  });
}

export function uploadTrailerVideoToCloudflare(options) {
  return uploadVideoToCloudflare({
    ...options,
    minimumDurationSeconds: TRAILER_UPLOAD_MIN_DURATION_SECONDS,
    playbackBaseUrl: "https://iframe.videodelivery.net",
    uploadPath: trailerVideoUploadPath,
  });
}
